import { useContext } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingErrorsAtom,
  buildStationRouteSaveRouteFields,
  createErrorResult,
  createSuccessResult,
  currentUserIdSelector,
  errors,
  errorsCrudContentAtomFamily,
  filterOnlyDirty,
  hasKeys,
  IStationRouteDto,
  IStationRouteMetadata,
  Log,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  STATION_ROUTE_FORM_EXCLUDED_METADATA_FIELDS,
  StationRouteApi,
  stationRouteEditHasMetadataChangedAtom,
  stationRouteHasUnsavedStationsChangeAtom,
  stationRouteMatchingMlPathIdsAtom,
  stationRouteMatchingMlPathUpdateIdAtom,
  useTimezone,
} from "@ryde/shared";

import { StationRouteContext, useCrudEditContext } from "../../contexts";
import { useHighlightEdited } from "../use-highlight-edited";

export const useStationRouteEdit = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const currentUserId = useRecoilValue(currentUserIdSelector);
  const { rideOpenerId } = useContext(StationRouteContext);
  const matchingMlPathUpdateId = useRecoilValue(
    stationRouteMatchingMlPathUpdateIdAtom,
  );
  const setMatchingMlPathIds = useSetRecoilState(
    stationRouteMatchingMlPathIdsAtom,
  );
  const setMatchingMlPathUpdateId = useSetRecoilState(
    stationRouteMatchingMlPathUpdateIdAtom,
  );
  const {
    data: { isDuplicate, save },
  } = useCrudEditContext<IStationRouteDto>();
  const {
    getValues,
    trigger,
    formState: { dirtyFields },
  } = useFormContext<IStationRouteDto>();
  const { setHighlightedRows } = useHighlightEdited("stationRoute");
  const setCrudServerErrors = useSetRecoilState(
    errorsCrudContentAtomFamily("stationRoute"),
  );
  const setApiServerErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setStationRouteHasUnsavedChange = useSetRecoilState(
    stationRouteHasUnsavedStationsChangeAtom,
  );
  const setStationRouteHasMetadataChange = useSetRecoilState(
    stationRouteEditHasMetadataChangedAtom,
  );

  const cleanFormFields = (
    formValues: Partial<IStationRouteDto> | undefined,
  ): Partial<IStationRouteDto> => {
    if (!formValues) {
      return {};
    }

    const { metadata: _metadata, ...formWithoutMetadata } = formValues;
    return isDuplicate
      ? formWithoutMetadata
      : filterOnlyDirty({
          formFields: formWithoutMetadata as Partial<IStationRouteDto>,
          dirtyFields,
          skipMetadata: true,
        });
  };

  const mergeStationRouteFormValuesToMetadata = (
    formValuesSnapshot: Partial<IStationRouteDto> | undefined,
    metadataComputed: IStationRouteMetadata,
  ): void => {
    if (
      !formValuesSnapshot?.metadata ||
      !hasKeys(formValuesSnapshot?.metadata)
    ) {
      return;
    }

    for (const metadataKey in formValuesSnapshot?.metadata) {
      if (
        STATION_ROUTE_FORM_EXCLUDED_METADATA_FIELDS.includes(
          metadataKey as keyof IStationRouteMetadata,
        )
      ) {
        continue;
      }

      const metadataValue =
        formValuesSnapshot.metadata[metadataKey as keyof IStationRouteMetadata];

      if (metadataValue !== undefined && metadataValue !== null) {
        (metadataComputed as Record<string, unknown>)[
          metadataKey as keyof IStationRouteMetadata
        ] =
          formValuesSnapshot.metadata[
            metadataKey as keyof IStationRouteMetadata
          ];
      }
    }
  };

  const saveStationRoute = useEvent(
    async (
      stationRouteDto: IStationRouteDto,
    ): Promise<SimpleResult<IStationRouteDto>> => {
      setCrudServerErrors(undefined);
      setApiServerErrors(undefined);

      if (!(await trigger())) {
        return new SimpleResult({
          error: t("errors.formNotValidSkipSavingStationRoute"),
          debugInfo: dirtyFields,
        });
      }

      const formValuesSnapshot = getValues() as
        Partial<IStationRouteDto> | undefined;
      const cleanedFormData = cleanFormFields(formValuesSnapshot);

      const metadata = {
        ...stationRouteDto.metadata,
      };
      mergeStationRouteFormValuesToMetadata(formValuesSnapshot, metadata);

      const { waypointIds, originStationId, destinationStationId, result } =
        buildStationRouteSaveRouteFields({
          stationInfos: stationRouteDto.computedData?.stationInfos,
          existingResult: stationRouteDto.result,
          timezone,
        });

      const excludedStationRouteIds =
        formValuesSnapshot?.virtualData?.excludedStationRouteIds || [];

      // Ride-opened save prefers the station-set canonical id so Next updates that row.
      const canonicalSaveId =
        (rideOpenerId ? matchingMlPathUpdateId : null) ??
        cleanedFormData.id ??
        stationRouteDto.id;

      const stationRouteForApi: Partial<IStationRouteDto> = {
        ...cleanedFormData,
        ...(canonicalSaveId ? { id: canonicalSaveId } : {}),
        metadata,
        customized: true,
        userUpdaterId: currentUserId,
        virtualData: {
          rideOpenerId,
          excludedStationRouteIds,
        },
        waypointIds,
        result,
        ...(originStationId !== undefined ? { originStationId } : {}),
        ...(destinationStationId !== undefined ? { destinationStationId } : {}),
      };

      Log.debug("Prepared stationRoute data for API:", stationRouteForApi);

      const apiResponse = await save?.(stationRouteForApi);

      if (!apiResponse) {
        return simpleResultErrorEmptyResponse(t);
      }

      if (apiResponse.success && apiResponse.data?.id) {
        setHighlightedRows([apiResponse.data.id]);
        setStationRouteHasUnsavedChange(false);
        setStationRouteHasMetadataChange(false);

        const composeDialogResultData: IStationRouteDto = {
          ...apiResponse.data,
          // Keep stations/waypoints from the form so ride can apply MLPath stops on Next.
          computedData: {
            ...apiResponse.data.computedData,
            ...stationRouteDto.computedData,
            stationInfos:
              apiResponse.data.computedData?.stationInfos ??
              stationRouteDto.computedData?.stationInfos,
          },
          result: apiResponse.data.result ?? result,
          virtualData: {
            excludedStationRouteIds,
            rideOpenerId,
          },
        };
        return createSuccessResult(composeDialogResultData);
      }

      return apiResponse;
    },
  );

  const convertToCustomized = useEvent(
    async ({
      stationRouteDto,
      shouldReplaceExisting,
    }: {
      stationRouteDto: IStationRouteDto | undefined;
      shouldReplaceExisting?: boolean;
    }): Promise<SimpleResult<{ stationRouteId: number }>> => {
      const stationRouteId = stationRouteDto?.id;
      if (!stationRouteId) {
        return createErrorResult(errors.common.invalidId);
      }

      if (stationRouteDto?.customized) {
        return createSuccessResult({ stationRouteId });
      }

      const apiResponse = await StationRouteApi.convertToCustomized({
        stationRouteId,
        shouldReplaceExisting,
      });

      if (!apiResponse) {
        return simpleResultErrorEmptyResponse(t);
      }

      return apiResponse;
    },
  );

  const cleanupStationRouteAtoms = useEvent(() => {
    setStationRouteHasUnsavedChange(false);
    setStationRouteHasMetadataChange(false);
    setMatchingMlPathIds([]);
    setMatchingMlPathUpdateId(null);
  });

  return { saveStationRoute, convertToCustomized, cleanupStationRouteAtoms };
};
